import { CKEditorEventAction, CKEditorEventDispatcher, useCKEditor } from "ckeditor4-react";
import SelectMergeFieldModal from "components/MergeField/SelectMergeFieldModal/SelectMergeFieldModal";
import { getCKEditorContentCss, getCKEditorTheme } from "components/theme/theme";
import React, { useEffect, useImperativeHandle } from "react";
import classNames from "classnames";

interface Props {
  toolbar: "Normal" | "InsertMergefield" | "CMS";
  content: string;
  onChange: (newContent: string, charactersRemaining: number) => void;
  onCtrlEnter?: (newContent: string, charactersRemaining: number) => void;
  onEsc?: () => void;
  focusOnMount?: boolean;
  error?: boolean;
  maxContentLength: number;
  mergeFields?: {
    sectionName: string;
    availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
  }
}

export interface HtmlEditorRef {
  /* Inserts text at current cursor position */
  insertText: (text: string) => void;
  /* Removes all instances of the given text from the editor content */
  removeText: (text: string) => void;
  /* Sets the entire editor content to the given text */
  setText: (text: string) => void;
}

const calculateRemainingCharacters = (htmlContent: string, maxContentLength: number) => {
  return maxContentLength - htmlContent.length;
}

declare const CKEDITOR: any;
// This makes CKEditor accept all content, even if it's not in the allowedContent list
// It's needed to insert <img/>, which CKEditor will otherwise block
CKEDITOR.config.allowedContent = true;
// Don't escape "« »" characters, which are used in merge fields. Without this, the merge field characters get replaced with &laquo; and &raquo;
//  and that breaks the merge field replacement later on
CKEDITOR.config.entities = false;
CKEDITOR.config.entities_additional = "#171;#187;";

export const HtmlEditor = React.forwardRef((props: Props, ref) => {
  const [ckEditorEl, setCkEditorEl] = React.useState<HTMLElement>(null);
  const [isMergeFieldModalOpen, setIsMergeFieldModalOpen] = React.useState(false);

  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      const editor = CKEDITOR.instances.ckEditor;
      editor.insertText(text);
    },
    removeText: (text: string) => {
      const editor = CKEDITOR.instances.ckEditor;
      const newData = editor.getData().replaceAll(text, "");
      editor.setData(newData);
    },
    setText: (text: string) => {
      const editor = CKEDITOR.instances.ckEditor;
      editor.setData(text);
    }
  }), []);

  useEffect(() => {
    if (ckEditor.editor == null) return;

    ckEditor.editor.setData(props.content);
  }, []);

  useEffect(() => {
    if (!props.mergeFields) return;

    window.ckEditorMergeFieldCallback = () => {
      setIsMergeFieldModalOpen(true);
    }

    return () => {
      window.ckEditorMergeFieldCallback = undefined;
    }
  }, []);

  const ckEditorChange = (evt) => {
    const newContent = evt.editor.getData();
    props.onChange(newContent, calculateRemainingCharacters(newContent, props.maxContentLength));
  }

  const onFileUploaded = (evt) => {
    // After file upload on CKEditor, getData() returns the html before the fileupload
    // https://github.com/ckeditor/ckeditor4/issues/1180
    setTimeout(() => ckEditorChange(evt));
  }

  const onMergeFieldSelected = (mergeField: string) => {
    setIsMergeFieldModalOpen(false);
    ckEditor.editor.insertText(mergeField);
    ckEditor.editor.focus();
  }

  const onSelectMergeFieldModalClose = () => {
    setIsMergeFieldModalOpen(false);
    ckEditor.editor.focus();
  }

  const dispatchCkEditorEvent: CKEditorEventDispatcher = ({ type, payload }) => {
    if (type === CKEditorEventAction.change) {
      ckEditorChange(payload);
    } else if (type === CKEditorEventAction.fileUploadResponse) {
      onFileUploaded(payload);
    } else if (type === CKEditorEventAction.key) {
      if (payload.data.keyCode === 27 /* ESC */) {
        props.onEsc?.();
      } else if (payload.data.keyCode === 1114125 /* CTRL+ENTER */) {
        const newContent = payload.editor.getData();
        props.onCtrlEnter?.(newContent, calculateRemainingCharacters(newContent, props.maxContentLength));
      }
    } else if (type === CKEditorEventAction.instanceReady) {
      const editor = payload.editor;
      // We need to trigger an onChange to properly initialize the content and character count
      //  in the parent component
      props.onChange(editor.getData(), calculateRemainingCharacters(editor.getData(), props.maxContentLength));

      if (props.focusOnMount) {
        setTimeout(() => {
          editor.focus();
        }, 5);
      }
    }
  }

  const ckEditor = useCKEditor({
    initContent: props.content,
    element: ckEditorEl,
    subscribeTo: ["change", "fileUploadResponse", "key", "instanceReady"],
    dispatchEvent: dispatchCkEditorEvent,
    config: {
      toolbar: props.toolbar,
      fillEmptyBlocks: false,
      tabSpaces: 0,
      enterMode: 2, // 1 = Enter creates <p>, 2 = Enter creates <br/>, 3 = Enter creates <div>
      skin: getCKEditorTheme(),
      contentsCss: window.CKEDITOR.getUrl(getCKEditorContentCss())
    }
  });

  return <>
    <div className={classNames({ "html-editor-component-error": props.error })}>
      <div id="ckEditor" ref={setCkEditorEl} />
    </div>
    {props.mergeFields && <SelectMergeFieldModal
      sectionName={props.mergeFields.sectionName}
      availableMergeFieldTopics={props.mergeFields.availableMergeFieldTopics}
      isOpen={isMergeFieldModalOpen}
      onClose={onSelectMergeFieldModalClose}
      onSelect={onMergeFieldSelected}
    />}
  </>
});
